import React from "react";
import { formatRelativeDate } from "../../utils/formatting";
import type { AdminUser } from "../../pages/AdminDashboard";

interface AdminUsersTabProps {
    users: AdminUser[];
    usersTotal: number;
    loadingUsers: boolean;
    usersPage: number;
    setUsersPage: React.Dispatch<React.SetStateAction<number>>;
    usersPageSize: number;
    setUsersPageSize: (size: number) => void;
    usersSearch: string;
    setUsersSearch: (search: string) => void;
    usersTier: "all" | "pro" | "free";
    setUsersTier: (tier: "all" | "pro" | "free") => void;
    usersRole: "all" | "admin" | "user";
    setUsersRole: (role: "all" | "admin" | "user") => void;
    usersSortBy: "createdAt" | "username" | "tier" | "lastSeenAt";
    setUsersSortBy: (sortBy: "createdAt" | "username" | "tier" | "lastSeenAt") => void;
    usersSortDir: "asc" | "desc";
    setUsersSortDir: (sortDir: "asc" | "desc") => void;
    usersTotalPages: number;
    maxBotsDraft: Record<string, string>;
    setMaxBotsDraft: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    updateUser: (userId: string, updates: Record<string, unknown>) => Promise<void>;
    saveMaxBotsOverride: (user: AdminUser) => Promise<void>;
    isCompactLayout: boolean;
}

const AdminUsersTab: React.FC<AdminUsersTabProps> = ({
    users,
    usersTotal,
    loadingUsers,
    usersPage,
    setUsersPage,
    usersPageSize,
    setUsersPageSize,
    usersSearch,
    setUsersSearch,
    usersTier,
    setUsersTier,
    usersRole,
    setUsersRole,
    usersSortBy,
    setUsersSortBy,
    usersSortDir,
    setUsersSortDir,
    usersTotalPages,
    maxBotsDraft,
    setMaxBotsDraft,
    updateUser,
    saveMaxBotsOverride,
    isCompactLayout,
}) => {
    return (
        <div className="glass-card" style={{ padding: "16px", borderRadius: "16px" }}>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                <input
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    placeholder="Search username or wallet"
                    className="input-dark"
                    style={{ minWidth: "160px", flex: "1 1 220px", padding: "8px" }}
                />
                <select value={usersTier} onChange={(e) => setUsersTier(e.target.value as "all" | "pro" | "free")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="all">All tiers</option>
                    <option value="pro">Pro</option>
                    <option value="free">Free</option>
                </select>
                <select value={usersRole} onChange={(e) => setUsersRole(e.target.value as "all" | "admin" | "user")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="all">All roles</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                </select>
                <select value={usersSortBy} onChange={(e) => setUsersSortBy(e.target.value as "createdAt" | "username" | "tier" | "lastSeenAt")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="createdAt">Sort: Created</option>
                    <option value="lastSeenAt">Sort: Last Seen</option>
                    <option value="username">Sort: Username</option>
                    <option value="tier">Sort: Tier</option>
                </select>
                <select value={usersSortDir} onChange={(e) => setUsersSortDir(e.target.value as "asc" | "desc")} className="input-dark" style={{ padding: "8px" }}>
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                </select>
                <select value={String(usersPageSize)} onChange={(e) => setUsersPageSize(Number(e.target.value))} className="input-dark" style={{ padding: "8px" }}>
                    <option value="10">10 / page</option>
                    <option value="25">25 / page</option>
                    <option value="50">50 / page</option>
                    <option value="100">100 / page</option>
                </select>
            </div>

            {loadingUsers ? (
                <p style={{ color: "#9ca3af" }}>Loading users...</p>
            ) : (
                <>
                    <div style={{ overflowX: isCompactLayout ? "visible" : "auto" }}>
                        <div style={{ display: "grid", gap: "10px", minWidth: isCompactLayout ? undefined : "1100px" }}>
                            {users.map((user) => (
                                <div
                                    key={user.id}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: isCompactLayout ? "1fr" : "1.5fr 0.7fr 0.8fr 0.9fr 1fr 1fr 1fr",
                                        gap: "12px",
                                        padding: "12px",
                                        borderRadius: "12px",
                                        background: "rgba(255,255,255,0.04)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        alignItems: "center",
                                        fontSize: "14px",
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{user.username}</div>
                                        <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                            Joined {new Date(user.createdAt).toLocaleDateString()}
                                        </div>
                                        <div style={{ color: "#6b7280", fontSize: "11px" }}>
                                            Last seen {formatRelativeDate(user.lastSeenAt)}
                                        </div>
                                    </div>
                                    <div style={{ color: isCompactLayout ? "#d1d5db" : undefined }}>Bots: {user.botCount}</div>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>Max bots</div>
                                        <div style={{ display: "flex", gap: "6px" }}>
                                            <input
                                                type="number"
                                                min={0}
                                                value={maxBotsDraft[user.id] ?? ""}
                                                placeholder={String(user.tierMaxBots)}
                                                onChange={(e) => setMaxBotsDraft((prev) => ({ ...prev, [user.id]: e.target.value }))}
                                                className="input-dark"
                                                style={{ width: isCompactLayout ? "100%" : "80px", padding: "6px", fontSize: "13px" }}
                                            />
                                            <button
                                                onClick={() => saveMaxBotsOverride(user)}
                                                style={{
                                                    padding: "6px 8px",
                                                    borderRadius: "6px",
                                                    border: "1px solid rgba(255,255,255,0.15)",
                                                    background: "rgba(255,255,255,0.06)",
                                                    color: "white",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <button
                                            onClick={() => updateUser(user.id, { tier: user.tier === "pro" ? "free" : "pro" })}
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: "8px",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                background: user.tier === "pro" ? "rgba(20, 241, 149, 0.2)" : "rgba(255,255,255,0.05)",
                                                color: user.tier === "pro" ? "#14f195" : "white",
                                                cursor: "pointer",
                                                width: "100%",
                                            }}
                                        >
                                            {user.tier === "pro" ? "Pro" : "Free"}
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            onClick={() => updateUser(user.id, { isAdmin: !user.isAdmin })}
                                            style={{
                                                padding: "6px 10px",
                                                borderRadius: "8px",
                                                border: "1px solid rgba(255,255,255,0.1)",
                                                background: user.isAdmin ? "rgba(153, 69, 255, 0.2)" : "rgba(255,255,255,0.05)",
                                                color: user.isAdmin ? "#c4b5fd" : "white",
                                                cursor: "pointer",
                                                width: "100%",
                                            }}
                                        >
                                            {user.isAdmin ? "Admin" : "User"}
                                        </button>
                                    </div>
                                    <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                        Wallet: {user.walletAddress ? `${user.walletAddress.slice(0, 4)}...${user.walletAddress.slice(-4)}` : "None"}
                                    </div>
                                    <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                                        Last login: {formatRelativeDate(user.lastLoginAt)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                            {usersTotal} users total
                        </span>
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <button
                                onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                                disabled={usersPage <= 1}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color: usersPage <= 1 ? "#6b7280" : "white",
                                    cursor: usersPage <= 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Prev
                            </button>
                            <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                                Page {usersPage} / {usersTotalPages}
                            </span>
                            <button
                                onClick={() => setUsersPage((p) => Math.min(usersTotalPages, p + 1))}
                                disabled={usersPage >= usersTotalPages}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    background: "rgba(255,255,255,0.05)",
                                    color: usersPage >= usersTotalPages ? "#6b7280" : "white",
                                    cursor: usersPage >= usersTotalPages ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminUsersTab;
